import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import PermissionRequired from "@surgia/ui/pages/PermissionRequired";
import { getLocalStorageItem } from "../pages/Catalog";

const userPermissions = getLocalStorageItem("permissions", []);

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermissions?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermissions = [],
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Access Redux global state for authentication
  const { globalState } = useSelector((state: RootState) => state.auth);
  const isReduxAuthenticated =
    globalState?.access_token && globalState?.subject_id;
  const hasPermission = requiredPermissions.every((permission) =>
    userPermissions.includes(permission)
  );

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Check BOTH OIDC authentication AND Redux state
  if (!isAuthenticated && !isReduxAuthenticated) {
    // Only redirect to login if trying to access a protected route
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If the user does not have the required permissions, show the permission required page
  if (!hasPermission && requiredPermissions?.length > 0) {
    return <PermissionRequired />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
